/**
 * @fileoverview Browser-side user/session helpers for permissions, auth state,
 * and local storage-backed profile access.
 */

/**
 * @param {Object} params
 * @param {string[]} params.grants - list of grants
 * @param {string} params.resource - resource (e.g. "*:*" or "partner:dailykos" or "ANY" to return true if the user has the specified permissionto for any resource)
 * @param {string} params.permission - permission (e.g. "user.read" or "partner.read")
 *
 * Accepts list of grants for a user, a resource and permission to check, and then true if grants
 * give access to the resource/permission.
 */
function checkPermission({ grants, resource, permission }) {
  const partnerType = resource.split(':')[0];
  const matchingGrants = grants.filter((grant) => grant.permission === permission && (
    resource === 'ANY' || grant.resource === resource || grant.resource === '*:*' || grant.resource === `${ partnerType }:*`
  ));
  return matchingGrants.length > 0;
}

/**
 * Retrieves the user's permission grants from localStorage.
 *
 * @returns {Object[]} Array of grant objects with permission and resource properties
 */
function getGrants() {
  const grants = localStorage.getItem('grants');
  if (!grants || grants === '') {
    return [];
  } else {
    return JSON.parse(grants);
  }
}

/**
 * Retrieves the current user's ID from localStorage.
 *
 * @returns {string|null} The user's auth0 ID, or null if not logged in
 */
function getUserId() {
  return localStorage.getItem('user_id');
}

/**
 * Retrieves the user's group memberships from localStorage.
 *
 * @returns {string[]} Array of group slugs the user belongs to
 */
function getUserGroups() {
  const groups = localStorage.getItem('groups');
  if (!groups || groups === '') {
    return [];
  } else {
    return JSON.parse(groups);
  }
}

/**
 * Retrieves the user's partner affiliations from localStorage.
 *
 * @returns {string[]} Array of partner slugs the user is affiliated with
 */
function getUserPartners() {
  const partners = localStorage.getItem('partners');
  if (!partners || partners === '') {
    return [];
  } else {
    return JSON.parse(partners);
  }
}

/**
 * Replaces the user's cached partner affiliations in localStorage.
 *
 * @param {string[]} partners - Partner slugs the user is affiliated with
 */
function setUserPartners(partners) {
  localStorage.setItem('partners', JSON.stringify(partners));
}

/**
 * Checks if the user has admin privileges.
 *
 * @param {Object} user - User object
 * @param {boolean} user.is_admin - Admin flag
 * @returns {boolean} True if user is an admin
 */
function isAdmin(user) {
  return user.is_admin;
}

/**
 * Checks if the current session is authenticated and not expired.
 *
 * @returns {boolean} True if access token exists and hasn't expired
 */
function isAuthenticated() {
  // Check whether the current time is past the
  // Access Token's expiry time
  try {
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    return new Date().getTime() < expiresAt;
  } catch {
    return false;
  }
}

/**
 * Checks if the user can adopt social batches (bulk voter adoption feature).
 *
 * @returns {boolean} True if user is in the socialbatchadopters group
 */
function canUserAdoptSocialBatches() {
  return getUserGroups().includes('socialbatchadopters');
}

/**
 * Checks if the user is qualified to adopt voters.
 *
 * @param {Object} user - User object
 * @param {string} user.qual_state - User's qualification state
 * @returns {boolean} True if user is 'qualified' or 'super_qualified'
 */
function isUserQualified(user) {
  return user.qual_state === 'qualified'
    || user.qual_state === 'super_qualified';
}

/**
 * Clears all session data from localStorage and sessionStorage.
 *
 * Used during logout to remove all cached user data, tokens, and
 * React Query cache. Ensures clean state for next login.
 *
 * @param {Object} [queryClient] - React Query client instance to clear cached queries
 */
function nullifySession(queryClient) {
  localStorage.removeItem('access_token');
  localStorage.removeItem('email');
  localStorage.removeItem('expires_at');
  localStorage.removeItem('grants');
  localStorage.removeItem('groups');
  localStorage.removeItem('id_token');
  localStorage.removeItem('is_admin');
  localStorage.removeItem('letter_adoption_override');
  localStorage.removeItem('partners');
  localStorage.removeItem('picture_url');
  localStorage.removeItem('user_id');
  localStorage.removeItem('user_qualified_state');
  localStorage.removeItem('next_partner_to_sign');
  sessionStorage.removeItem('emailToken');
  if (queryClient) {
    queryClient.removeQueries('campaigns');
    queryClient.removeQueries('letterKits');
    queryClient.removeQueries('users');
    queryClient.clear();
    localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');
  }
}

export {
  canUserAdoptSocialBatches,
  checkPermission,
  getGrants,
  getUserId,
  getUserPartners,
  isAdmin,
  isAuthenticated,
  isUserQualified,
  nullifySession,
  setUserPartners,
};
